import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { projectsAPI } from "../../services/api";
import { useAuth } from "../context/AuthContext";

const CreateProject: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: [] as string[],
    projectType: "digital_marketing",
    link: "",
    keyFindings: [] as string[],
    isFeatured: false,
  });
  // Images are now uploaded files rather than URLs.
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [thumbnailFiles, setThumbnailFiles] = useState<File[]>([]);
  const [thumbnailPreviews, setThumbnailPreviews] = useState<string[]>([]);
  const [newKeyFinding, setNewKeyFinding] = useState("");

  const projectTypes = [
    { value: "digital_marketing", label: "Digital Marketing" },
    { value: "website", label: "Website" },
  ];

  const categories = [
    { value: "smm", label: "Social Media Marketing" },
    { value: "graphic_design", label: "Custom Graphic Design" },
    { value: "ui_ux", label: "UI/UX" },
    { value: "web_development", label: "Web Design and Development" },
    { value: "seo", label: "Search Engine Optimization" },
    { value: "ad_commercial", label: "Advertisement and Commercial" },
    { value: "event_management", label: "Event Management" },
    { value: "product_shoot", label: "Creative Product Shoot" },
  ];

  const handleCategoryChange = (cat: string) => {
    setFormData((prev) => ({
      ...prev,
      category: prev.category.includes(cat)
        ? prev.category.filter((c) => c !== cat)
        : [...prev.category, cat],
    }));
  };

  // Release object URLs created for previews on unmount.
  useEffect(() => {
    return () => {
      if (mainImagePreview) URL.revokeObjectURL(mainImagePreview);
      thumbnailPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (mainImagePreview) URL.revokeObjectURL(mainImagePreview);
    setMainImageFile(file);
    setMainImagePreview(URL.createObjectURL(file));
  };

  const handleThumbnailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;
    // Cap the combined set at 10 (matches the multer maxCount on the server).
    const room = 10 - thumbnailFiles.length;
    const toAdd = selected.slice(0, Math.max(room, 0));
    setThumbnailFiles((prev) => [...prev, ...toAdd]);
    setThumbnailPreviews((prev) => [
      ...prev,
      ...toAdd.map((f) => URL.createObjectURL(f)),
    ]);
    // Allow re-selecting the same file later.
    e.target.value = "";
  };

  const removeThumbnail = (index: number) => {
    URL.revokeObjectURL(thumbnailPreviews[index]);
    setThumbnailFiles((prev) => prev.filter((_, i) => i !== index));
    setThumbnailPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const addKeyFinding = () => {
    if (newKeyFinding.trim()) {
      setFormData((prev) => ({
        ...prev,
        keyFindings: [...prev.keyFindings, newKeyFinding.trim()],
      }));
      setNewKeyFinding("");
    }
  };

  const removeKeyFinding = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      keyFindings: prev.keyFindings.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.category.length === 0) {
      alert("Please select at least one category");
      return;
    }

    if (!mainImageFile) {
      alert("Please select a main image");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);
      // Arrays are sent as JSON strings; the server JSON.parses them.
      data.append("category", JSON.stringify(formData.category));
      data.append("projectType", formData.projectType);
      data.append("link", formData.link.trim());
      data.append("keyFindings", JSON.stringify(formData.keyFindings));
      data.append("isFeatured", String(formData.isFeatured));
      data.append("mainImage", mainImageFile);
      thumbnailFiles.forEach((file) => data.append("thumbnailImages", file));

      await projectsAPI.create(data);
      alert("Project created successfully!");
      navigate("/admin/projects");
    } catch (error: any) {
      alert("Error creating project: " + error.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Create New Project
          </h1>
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/projects"
              className="text-gray-600 hover:text-gray-900 transition"
            >
              ← Back to Projects
            </Link>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-md p-8 space-y-6"
        >
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Project Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
              placeholder="Enter project title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              required
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition resize-none"
              placeholder="Enter project description"
            />
          </div>

          {/* Project Type — the tab this lands under on the public site. */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Project Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {projectTypes.map((type) => (
                <label
                  key={type.value}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                    formData.projectType === type.value
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <input
                    type="radio"
                    name="projectType"
                    value={type.value}
                    checked={formData.projectType === type.value}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        projectType: type.value,
                      }))
                    }
                    className="w-5 h-5 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="ml-3 text-gray-700 font-medium">
                    {type.label}
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Decides which filter tab the project appears under on the projects
              page. Separate from the categories below.
            </p>
          </div>

          {/* Website Link */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Website Link{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="url"
              value={formData.link}
              onChange={(e) =>
                setFormData({ ...formData, link: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
              placeholder="https://example.com"
            />
            <p className="mt-1 text-sm text-gray-500">
              Shown as an arrow beside the title in the project popup. Must
              start with http:// or https://.
            </p>
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Categories <span className="text-red-500">*</span> (Select
              multiple)
            </label>
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat) => (
                <label
                  key={cat.value}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                    formData.category.includes(cat.value)
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.category.includes(cat.value)}
                    onChange={() => handleCategoryChange(cat.value)}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-3 text-gray-700 font-medium">
                    {cat.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Main Image */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Main Image <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleMainImageChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {mainImagePreview && (
              <div className="mt-4">
                <img
                  src={mainImagePreview}
                  alt="Main preview"
                  className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-gray-200"
                />
              </div>
            )}
          </div>

          {/* Thumbnail Images */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Thumbnail Images (Max 10)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleThumbnailsChange}
              disabled={thumbnailFiles.length >= 10}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
            />
            <p className="mt-1 text-sm text-gray-500">
              {thumbnailFiles.length}/10 selected
            </p>
            {thumbnailPreviews.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                {thumbnailPreviews.map((url, index) => (
                  <div key={index} className="relative">
                    <img
                      src={url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-24 object-cover rounded border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeThumbnail(index)}
                      className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white w-6 h-6 rounded-full flex items-center justify-center transition"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Key Findings */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Key Findings
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newKeyFinding}
                onChange={(e) => setNewKeyFinding(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                placeholder="Enter a key finding"
              />
              <button
                type="button"
                onClick={addKeyFinding}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition"
              >
                Add
              </button>
            </div>
            {formData.keyFindings.length > 0 && (
              <div className="space-y-2">
                {formData.keyFindings.map((finding, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="flex-1 text-gray-700">{finding}</span>
                    <button
                      type="button"
                      onClick={() => removeKeyFinding(index)}
                      className="bg-red-600 hover:bg-red-700 text-white w-8 h-8 rounded-full flex items-center justify-center transition"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Featured Toggle */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Featured Project
            </label>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Mark as Featured</p>
                <p className="text-sm text-gray-600 mt-1">
                  Featured projects will be highlighted on the homepage
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    isFeatured: !prev.isFeatured,
                  }))
                }
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  formData.isFeatured ? "bg-indigo-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    formData.isFeatured ? "translate-x-7" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            {formData.isFeatured && (
              <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-sm text-indigo-700 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  This project is marked as featured
                </p>
              </div>
            )}
          </div>
          {/* Form Actions */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate("/admin/projects")}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-semibold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating..." : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProject;
